// @ts-nocheck
/*
 * Port of views/dashboard/outcoming-summary/list.js (758L). BYTE-IDENTICAL.
 *
 * Outgoing Summary â€” near-mirror of Incoming Summary with these deltas:
 *  - Hits API_OUTCOMING_SUMMARY_LIST / OUTCOMING_SUMMARY_LIST resource
 *  - View â†’ `/did-report/â€¦?type=outgoing`
 *  - Title always "Outgoing Summary" (no role branching)
 *  - Export CSV filename is "outgoing-summary"
 *
 * V1 QUIRKS preserved verbatim (see IncomingSummaryPage header for
 * details): first useEffect has no dep array, injects style tag on
 * mount, side-panel detail view rendered but unreachable via the
 * default `viewDetails` navigation path.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getApiCall, postApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import {
  API_OUTCOMING_SUMMARY_LIST,
  DOWNLOAD_REPORT,
  API_INCOMING_REPORT_BY_DID_LIST,
} from '@/service/url';
import moment from 'moment';
import { getUser, Storage } from '@/utilities/setting';
import { useNavigate } from 'react-router-dom';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Eye, X } from 'lucide-react';
import { CallEngineTracesButton } from '@/features/ivr-reports/components/CallEngineTraces';
import {
  CallTranscriptButton,
  useCanViewTranscripts,
  useTranscriptAvailability,
} from '@/features/transcripts/CallTranscript';
import {
  loadPortalReportData,
  PORTAL_REPORT_RESOURCES,
} from '@/utilities/portalReportRefresh';
import {
  AutoRefreshCountdownButton,
  useAutoRefreshCountdown,
} from '@/hooks/useAutoRefreshCountdown';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { canDownloadReports } from '@/utilities/promptUploadAccess';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { USER_ROLES } from '@/utilities/constant';
import { CalendarDateInput, CalendarDateTimeInput } from '@/components/ui/calendar-inputs';

export const formatDateTime = (dateString: any) => {
  if (!dateString) return '';
  const isoString = dateString.replace(' ', 'T') + 'Z';
  const date = new Date(isoString);
  const istString = date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour12: false,
  });
  const [datePart, timePart] = istString.split(', ');
  const [day, month, year] = datePart.split('/');
  return `${year}-${month}-${day} ${timePart}`;
};

const formatIstStamp = (
  value: any,
  options: Intl.DateTimeFormatOptions,
) => {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { ...options, timeZone: 'UTC' });
};

const getDefaultDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date();
  return { start, end };
};

const dateToInputStr = (d: any) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const inputStrToLocalDate = (str: any) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
  );
};

const OutcomingSummary = () => {
  const [outcomingSummary, setOutcomingSummary] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const { start, end } = getDefaultDates();
  const [startDate, setStartDate] = useState<any>(start);
  const [endDate, setEndDate] = useState<any>(end);
  const [selectedDid, setSelectedDid] = useState<any>(null);
  const [didOptions, setDidOptions] = useState<any[]>([]);
  const [showToastModal, setShowToastModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [outcomingReport, setOutcomingReport] = useState<any[]>([]);
  const canViewTranscripts = useCanViewTranscripts();
  // One availability lookup for the whole detail table, not one per row.
  const transcriptLineCount = useTranscriptAvailability(
    useMemo(() => outcomingReport.map((r: any) => r?.trace_id), [outcomingReport])
  );
  const [detailStartDate, setDetailStartDate] = useState('');
  const [detailEndDate, setDetailEndDate] = useState('');
  const [detailDid, setDetailDid] = useState<any>(null);
  const navigate = useNavigate();
  const [loggedInUserId, setLoggedInUserId] = useState<any>(null);
  const [usernames, setUsernames] = useState<any[]>([]);
  const [selectedUsername, setSelectedUsername] = useState<any>(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState<any>(null);
  const [callIdSearch, setCallIdSearch] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User data not found in Storage');
        return;
      }
      let user: any;
      try {
        user = JSON.parse(rawUser);
        if (user) {
          setLoggedInUserId(user?.id);
          setLoggedInUserRole(user?.role);
        }
      } catch (e) {
        console.error('Invalid JSON in Storage user:', e);
        return;
      }
    };
    fetchUserData();
  });

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-scrollbar-panel::-webkit-scrollbar { width: 20px; }
      .custom-scrollbar-panel::-webkit-scrollbar-track { background: #f1f1f1; }
      .custom-scrollbar-panel::-webkit-scrollbar-thumb { background: #888; border-radius: 8px; }
      .custom-scrollbar-panel::-webkit-scrollbar-thumb:hover { background: #555; }
      .custom-scrollbar-panel { scrollbar-width: auto; scrollbar-color: #888 #f1f1f1; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const showToasts = (message: string) => {
    toast.success(message);
  };

  const handleFilter = () => {
    const start = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const end = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
    fetchOutcomingSummary(start, end, true, true, callIdSearch);
  };

  const handleExport = async () => {
    try {
      exportToCSV({
        fileName: 'outgoing-summary',
        data: filteredData,
        columns: [
          { header: 'Did', accessor: 'did' },
          { header: 'Username', accessor: 'user_username' },
          {
            header: 'Schedule Date',
            accessor: (row: any) => formatDateTime(row.schedule_date),
          },
          // created_at / updated_at stored in IST already (unlike UTC schedule_date) - show raw
          { header: 'Created At', accessor: (row: any) => row.created_at || '-' },
          { header: 'Updated At', accessor: (row: any) => row.updated_at || '-' },
          { header: 'Total Nos', accessor: 'nos_processed' },
          { header: 'Connected Calls', accessor: 'connected_calls' },
          // { header: 'SMS Count', accessor: 'sms_count' },
          { header: 'Billing Duration', accessor: 'billing_duration' },
          // { header: 'DTMF Count', accessor: 'dtmf_count' },
          // { header: 'Retry Count', accessor: 'retry_count' },
          // {
          //   header: 'Webhook Count',
          //   accessor: (row: any) => row.webhook_count || 0,
          // },
        ],
      });
    } finally {
      /* noop */
    }
  };

  const applyOutcomingSummaryList = (resp: any) => {
    if (!resp) return;
    const updatedList = resp.map((val: any, idx: number) => {
      val['key'] = idx + 1;
      return val;
    });
    setOutcomingSummary(updatedList);
    setFilteredData(updatedList);

    const uniqueDids = [
      ...new Set(
        updatedList
          .map((item: any) => item.did)
          .filter((did: any) => did != null && String(did).trim() !== ''),
      ),
    ].map((did: any) => ({ label: String(did).trim(), value: String(did).trim() }));

    const uniqueUsers = [
      ...new Set(
        updatedList
          .map((item: any) => item.user_username)
          .filter((u: any) => u != null && String(u).trim() !== ''),
      ),
    ].map((u: any) => ({ label: String(u).trim(), value: String(u).trim() }));

    setDidOptions(uniqueDids);
    setUsernames(uniqueUsers);
  };

  const fetchOutcomingSummary = async (
    start: any,
    end: any,
    force = false,
    showLoader = true,
    callIdSearchValue = '',
  ) => {
    if (!loggedInUserId || !loggedInUserRole) return;
    if (showLoader) setLoading(true);
    const startDate = start || moment().startOf('month').format('YYYY-MM-DD');
    const endDate = end || moment().format('YYYY-MM-DD');
    const idSearch = (callIdSearchValue || '').trim();

    try {
      const result: any = await loadPortalReportData({
        resource: PORTAL_REPORT_RESOURCES.OUTCOMING_SUMMARY_LIST,
        userId: loggedInUserId,
        role: loggedInUserRole,
        params: { start_date: startDate, end_date: endDate, call_id_search: idSearch },
        force,
        onBlocked: (remainingMs: number) => {
          toast.info(
            `Outgoing summary can be refreshed after ${Math.ceil(
              remainingMs / 1000,
            )} seconds.`,
          );
        },
        fetchFn: () =>
          getApiCall(
            `${API_OUTCOMING_SUMMARY_LIST}?start_date=${startDate}&end_date=${endDate}${
              idSearch ? `&call_id_search=${encodeURIComponent(idSearch)}` : ''
            }`,
          ),
      });
      applyOutcomingSummaryList(result.data);
    } catch (error) {
      toast.error(friendlyApiError(error));
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const refreshOutcomingSummary = useCallback(
    (force = false, showLoader = false) => {
      const start = startDate
        ? moment(startDate).format('YYYY-MM-DD')
        : moment().startOf('month').format('YYYY-MM-DD');
      const end = endDate
        ? moment(endDate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
      return fetchOutcomingSummary(start, end, force, showLoader, callIdSearch);
    },
    [startDate, endDate, loggedInUserId, loggedInUserRole, callIdSearch],
  );

  const { refreshCountdownSec, resetCountdown } = useAutoRefreshCountdown({
    enabled: Boolean(loggedInUserId && loggedInUserRole),
    onRefresh: () => refreshOutcomingSummary(false, false),
  });

  useEffect(() => {
    if (loggedInUserId && loggedInUserRole) {
      fetchOutcomingSummary(null, null);
    }
  }, [loggedInUserId, loggedInUserRole]);

  const handleReset = () => {
    const { start, end } = getDefaultDates();
    setStartDate(start);
    setEndDate(end);
    setSelectedDid(null);
    setSelectedUsername(null);
    setCallIdSearch('');
    fetchOutcomingSummary(
      moment(start).format('YYYY-MM-DD'),
      moment(end).format('YYYY-MM-DD'),
      true,
      true,
      '',
    );
  };

  const downloadFile = (rowData: any) => {
    if (!rowData) {
      console.error('Row data is missing.');
      return;
    }
    const params: any = {
      did: rowData.did,
      user_id: loggedInUserId,
      status: 0,
      report_type: 'full',
      schedule_date: rowData.schedule_date,
    };

    postApiCall(DOWNLOAD_REPORT, params)
      .then(() => showToasts('Your Report Generation is in-progress'))
      .catch((err: any) => toast.error(friendlyApiError(err)));
  };

  const viewDetails = (rowData: any) => {
    const scheduleDate = rowData?.schedule_date;
    const istDay = moment.utc(scheduleDate).utcOffset(330).format('YYYY-MM-DD');
    setDetailDid(rowData.did);
    const defaultStartDate = `${istDay} 00:00:00`;
    const defaultEndDate = `${istDay} 23:59:59`;

    navigate(
      `/did-report/${rowData.did}/${defaultStartDate}/${defaultEndDate}?type=outgoing`,
    );
  };

  const handleDetailFilter = () => {
    getApiCall(
      `${API_INCOMING_REPORT_BY_DID_LIST}/${detailDid}/${detailStartDate}/${detailEndDate}`,
    )
      .then((resp: any) => {
        if (resp) setOutcomingReport(resp);
      })
      .catch((error) => console.log(error));
    setShowSidePanel(true);
  };

  const handleDetailClearFilter = (rowData: any) => {
    viewDetails(rowData);
  };

  useEffect(() => {
    let filtered = [...outcomingSummary];
    if (selectedDid)
      filtered = filtered.filter((d: any) => d.did == selectedDid);
    if (selectedUsername)
      filtered = filtered.filter(
        (d: any) => d.user_username === selectedUsername,
      );
    setFilteredData(filtered);
  }, [outcomingSummary, selectedDid, selectedUsername]);

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Outgoing Summary',
    'read',
  );

  if (
    Object.keys(moduleOperationsForReadPermissionCheck).length > 0 &&
    !canRead
  ) {
    return (
      <div
        className="text-center p-4"
        style={{
          marginTop: '50px',
          marginBottom: '50px',
          fontSize: '16px',
        }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    { accessorKey: 'did', header: 'Did' },
    { accessorKey: 'user_username', header: 'Username' },
    {
      accessorKey: 'schedule_date',
      header: 'Schedule Date',
      cell: ({ row }: any) => formatDateTime(row.original.schedule_date),
    },
    // created_at / updated_at stored in IST already (unlike UTC schedule_date) - show raw
    { accessorKey: 'created_at', header: 'Created At', cell: ({ row }: any) => row.original.created_at || '-' },
    { accessorKey: 'updated_at', header: 'Updated At', cell: ({ row }: any) => row.original.updated_at || '-' },
    { accessorKey: 'nos_processed', header: 'Total Nos' },
    { accessorKey: 'connected_calls', header: 'Connected Calls' },
    // { accessorKey: 'sms_count', header: 'SMS Count' },
    { accessorKey: 'billing_duration', header: 'Billing Duration' },
    // { accessorKey: 'dtmf_count', header: 'Dtmf Count' },
    // { accessorKey: 'retry_count', header: 'Retry Count' },
    // {
    //   accessorKey: 'webhook_count',
    //   header: 'Webhook Count',
    //   cell: ({ row }: any) => row.original.webhook_count || 0,
    // },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              downloadFile(row.original);
            }}
            title="Download"
            aria-label="Download"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              viewDetails(row.original);
            }}
            title="View details"
            aria-label="View details"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Outgoing Summary"
        description="Outbound call totals with per-day counts and connection rate."
        actions={
          <div className="flex items-center gap-2">
            <AutoRefreshCountdownButton
              countdownSec={refreshCountdownSec}
              title="Auto-refresh outgoing summary"
              onManualRefresh={() => {
                refreshOutcomingSummary(true, false);
                resetCountdown();
              }}
            />
          </div>
        }
      />
      <div className="mt-4">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              From
            </label>
            <CalendarDateInput
              value={dateToInputStr(startDate)}
              onChange={(e) => setStartDate(inputStrToLocalDate(e))}
              min={dateToInputStr(moment().subtract(6, 'months').toDate())}
              max={dateToInputStr(new Date())}
              className="input-embossed h-10 w-[170px] rounded-md px-3 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              To
            </label>
            <CalendarDateInput
              value={dateToInputStr(endDate)}
              onChange={(e) => setEndDate(inputStrToLocalDate(e))}
              min={dateToInputStr(startDate)}
              max={dateToInputStr(new Date())}
              className="input-embossed h-10 w-[170px] rounded-md px-3 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              DID
            </label>
            <select
              value={selectedDid || ''}
              onChange={(e) => setSelectedDid(e.target.value || null)}
              className="input-embossed h-10 w-[200px] rounded-md px-3 text-sm"
            >
              <option value="">All DIDs</option>
              {didOptions.map((did: any) => (
                <option key={did.value} value={did.value}>
                  {did.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Username
            </label>
            <select
              value={selectedUsername || ''}
              onChange={(e) => setSelectedUsername(e.target.value || null)}
              className="input-embossed h-10 w-[200px] rounded-md px-3 text-sm"
            >
              <option value="">All users</option>
              {usernames.map((user: any) => (
                <option key={user.value} value={user.value}>
                  {user.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Call ID / Trace ID / SIP Call ID
            </label>
            <input
              type="text"
              value={callIdSearch}
              onChange={(e) => setCallIdSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFilter();
              }}
              placeholder="Search by call_sid, trace_id, sip_call_id"
              className="input-embossed h-10 w-[260px] rounded-md px-3 text-sm"
            />
          </div>
          <Button size="sm" onClick={handleFilter}>
            Filter
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            Reset
          </Button>
          {filteredData.length > 0 && canDownloadReports() && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              className="ml-auto gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
          )}
        </div>

        <div className="scroll-container">
          <DataTable
            tableId="outgoing-summary:v2"
            columns={columns}
            data={filteredData}
            pageSize={10}
            emptyTitle="No records"
            disableExport
          />
        </div>
      </div>

      {showSidePanel && (
        <div
          className="custom-scrollbar-panel"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '1500px',
            height: '100%',
            backgroundColor: '#fff',
            borderLeft: '1px solid #ddd',
            boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
            zIndex: 1050,
            overflowY: 'auto',
            transition: 'transform 0.3s ease-in-out',
            padding: '15px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <h5 style={{ margin: 0 }}>Detail View</h5>
            <button
              onClick={() => setShowSidePanel(false)}
              style={{
                border: 'none',
                backgroundColor: '#dc3545',
                color: '#fff',
                borderRadius: '3px',
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            >
              <X
                aria-hidden="true"
                style={{ width: 16, height: 16 }}
              />
            </button>
          </div>

          <div>
            <p>
              <strong>DID:</strong> {selectedRow?.did}
            </p>
            <p>
              <strong>Username:</strong> {selectedRow?.user_username}
            </p>
            <p>
              <strong>Schedule Date:</strong>{' '}
              {new Date(selectedRow?.schedule_date).toUTCString()}
            </p>
            <p>
              <strong>Nos Processed:</strong> {selectedRow?.nos_processed}
            </p>
            <p>
              <strong>Connected Calls:</strong>{' '}
              {selectedRow?.connected_calls}
            </p>
            <p>
              <strong>Total Nos:</strong> {selectedRow?.total_nos}
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '20px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label>
                <strong>Start Date:</strong>
              </label>
              <CalendarDateTimeInput
                value={detailStartDate}
                onChange={(e) => setDetailStartDate(e)}
                style={{ padding: '5px', minWidth: '220px' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label>
                <strong>End Date:</strong>
              </label>
              <CalendarDateTimeInput
                value={detailEndDate}
                onChange={(e) => setDetailEndDate(e)}
                style={{ padding: '5px', minWidth: '220px' }}
              />
            </div>

            <button
              onClick={handleDetailFilter}
              style={{
                marginTop: '22px',
                padding: '7px 12px',
                backgroundColor: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Filter
            </button>

            <button
              onClick={() => handleDetailClearFilter(selectedRow)}
              style={{
                marginTop: '22px',
                padding: '7px 12px',
                backgroundColor: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Clear Filter
            </button>
          </div>

          <div className="scroll-container">
            <DataTable
              tableId="outgoing-summary-detail"
              columns={[
                { accessorKey: 'user_id', header: 'User Id' },
                { accessorKey: 'caller_number', header: 'Caller Number' },
                { accessorKey: 'callee_number', header: 'Callee Number' },
                {
                  accessorKey: 'start_time',
                  header: 'Start Time',
                  cell: ({ row }: any) =>
                    formatIstStamp(row.original.start_time, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    }),
                },
                {
                  accessorKey: 'end_time',
                  header: 'End Time',
                  cell: ({ row }: any) =>
                    formatIstStamp(row.original.end_time, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    }),
                },
                ...(canViewTranscripts
                  ? [
                      {
                        id: 'transcript',
                        header: 'Transcript',
                        enableSorting: false,
                        cell: ({ row }: any) => (
                          <CallTranscriptButton
                            traceId={row.original.trace_id}
                            lineCount={transcriptLineCount(row.original.trace_id)}
                            className="h-8 w-8"
                          />
                        ),
                      },
                    ]
                  : []),
                ...(loggedInUserRole === USER_ROLES.SUPERADMIN
                  ? [
                      {
                        id: 'engine_traces',
                        header: 'Engine traces',
                        enableSorting: false,
                        cell: ({ row }: any) => (
                          <CallEngineTracesButton
                            callSid={row.original.call_sid}
                            sipCallId={row.original.sip_call_id}
                            className="h-8 w-8"
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
              data={outcomingReport}
              pageSize={10}
              emptyTitle="No records"
              disableExport
            />
          </div>
        </div>
      )}
    </FillPage>
  );
};

export default OutcomingSummary;
